import React, {useCallback, useEffect, useMemo, useState} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {WindowModeType} from 'types'
import {
  calcTimeDiff,
  formatHours,
  getCurrencyValue,
  parseDate,
  showSnackbar,
} from 'utils'
import {useWindowMode} from 'windows'
import {Icon, ModalLoading, Paragraph} from 'common/components'
import firebase from 'lib/firebase'
import convertUnit from 'lib/unit'
import {
  TransactionPaymentHistoryParams,
  PaymentInstructions,
  VirtualAccountData,
  RetailData,
  PaymentHistoryInfoItem,
} from 'pages'
import {useSelector} from 'lib/redux'
import {useTheme} from 'themes'

interface StyledContainerProps {
  mode: WindowModeType
}

const StyledContainer = styled.div<StyledContainerProps>`
  ${({mode, theme}) => ({
    flexDirection: mode === 'website' ? 'row' : 'column',
    margin: convertUnit(mode === 'website' ? 25 : 0),
    padding: convertUnit(mode === 'website' ? 50 : 25),
    backgroundColor: theme.white_1,
  })}
  display: flex;
  box-sizing: border-box;

  overflow-y: scroll;
  ::-webkit-scrollbar {
    background-color: ${({theme}) => theme.white_1};
    width: ${convertUnit(7)};
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${({theme}) => theme.primary_5};
    background-clip: content-box;
    border-radius: ${convertUnit(8)};
  }
`

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const StyledRowSection = styled.div`
  display: flex;
  flex-direction: row;
`

const StyledRightSection = styled(StyledSection)<StyledContainerProps>`
  ${({mode}) => ({
    marginLeft: mode === 'website' ? convertUnit(50) : undefined,
    marginTop: mode === 'website' ? undefined : convertUnit(25),
  })}
`

const StyledTitle = styled(Paragraph)``

const StyledTitleContainer = styled.div`
  display: flex;
  align-items: center;
`

const StyledDateContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledDurationText = styled(Paragraph)`
  margin-bottom: ${convertUnit(8)};
`

const StyledDescriptionContainer = styled.div`
  ${({theme}) => ({
    backgroundColor: theme.white_2,
  })}
  display: flex;
  flex-direction: column;
  margin-top: ${convertUnit(10)};
  padding: ${convertUnit(10)};
`

const StyledMessageContainer = styled.div`
  ${({theme}) => ({
    backgroundColor: theme.white_4,
  })}
  border-radius: ${convertUnit(8)};
  width: 100%;
  display: flex;
  box-sizing: border-box;
  padding: ${convertUnit(8)} ${convertUnit(16)};
  margin-top: ${convertUnit(16)};
`

const StyledCopyIcon = styled(Icon)`
  margin-right: ${convertUnit(8)};
`

const StyledBadge = styled.div`
  width: fit-content;
  background-color: ${({theme}) => theme.primary_1};
  padding: ${convertUnit(2)} ${convertUnit(8)};
  border-radius: ${convertUnit(6)};
  margin-bottom: ${convertUnit(8)};
`

export default function PaymentHistory({
  invoiceId,
  transactionDate,
  paymentType,
  paymentMethod,
}: TransactionPaymentHistoryParams) {
  const {translate} = useTranslation()
  const [virtualAccount, setVirtualAccount] = useState<VirtualAccountData>({
    account_number: '',
    bank_code: '',
    expected_amount: 0,
    expiration_date: '',
    external_id: '',
    id: '',
    name: '',
    owner_id: '',
    status: '',
  })
  const [retail, setRetail] = useState<RetailData>({
    expected_amount: 0,
    expiration_date: '',
    id: '',
    payment_code: '',
    retail_outlet_name: '',
  })
  const [currentDate, setCurrentDate] = useState(new Date())
  const [timeDiff, setTimeDiff] = useState(
    calcTimeDiff(
      currentDate,
      retail.expected_amount === 0
        ? virtualAccount.expiration_date
        : retail.expiration_date,
    ),
  )
  const mode = useWindowMode()
  const [loading, setLoading] = useState(true)
  const name = useSelector('user')
  const theme = useTheme()

  const handleFirestore = useCallback(
    () =>
      firebase
        .firestore()
        .collection(process.env.FIRESTORE_COLLECTION_PAYMENT!)
        .doc(invoiceId)
        .onSnapshot((snapshot) => {
          const data = snapshot.data()!
          if (data !== undefined) {
            setVirtualAccount(data.virtual_account)
            setRetail(data.retail_outlet)
            setLoading(false)
          }
        }),
    [invoiceId],
  )
  useEffect(handleFirestore, [handleFirestore])

  const details = useMemo(() => {
    if (retail.expected_amount !== 0) {
      return {
        expected_amount: retail.expected_amount,
        expiration_date: retail.expiration_date,
        code: retail.payment_code,
        name: retail.retail_outlet_name,
      }
    }
    return {
      expected_amount: virtualAccount.expected_amount,
      expiration_date: virtualAccount.expiration_date,
      code: virtualAccount.account_number,
      name: virtualAccount.bank_code,
    }
  }, [
    retail.expected_amount,
    retail.expiration_date,
    retail.payment_code,
    retail.retail_outlet_name,
    virtualAccount.account_number,
    virtualAccount.bank_code,
    virtualAccount.expected_amount,
    virtualAccount.expiration_date,
  ])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentDate(new Date())
    }, 1000)

    return () => clearTimeout(timeoutId)
  }, [currentDate])

  useEffect(() => {
    setTimeDiff(calcTimeDiff(currentDate, details.expiration_date))
  }, [currentDate, details.expiration_date, retail.expected_amount])

  const typeDetails = useCallback(() => {
    switch (paymentType) {
      case 'Retail': {
        return {
          title: paymentMethod.charAt(0) + paymentMethod.slice(1).toLowerCase(),
          container: (
            <StyledDescriptionContainer>
              <StyledTitle>
                {translate('giftShop:retailDescription')}
              </StyledTitle>
              <StyledTitle>
                {translate('giftShop:retailDescription', {
                  context: 'second',
                  Amount: getCurrencyValue(retail.expected_amount),
                })}
              </StyledTitle>
              <StyledTitle>
                {translate('giftShop:retailDescription', {
                  context: 'third',
                })}
              </StyledTitle>
              <StyledTitle>
                {translate('giftShop:retailDescription', {
                  context: 'fourth',
                })}
              </StyledTitle>
            </StyledDescriptionContainer>
          ),
        }
      }
      case 'Virtual Account': {
        return {
          title: translate('giftShop:virtualAccountMethod', {
            method: paymentMethod,
          }),
          container: (
            <PaymentInstructions
              bank={paymentMethod}
              accountNumber={virtualAccount.account_number}
              totalPrice={virtualAccount.expected_amount}
            />
          ),
        }
      }
      default: {
        return {
          title: translate('giftShop:virtualAccountMethod', {
            method: paymentMethod,
          }),
          container: (
            <PaymentInstructions
              bank={paymentMethod}
              accountNumber={virtualAccount.account_number}
              totalPrice={virtualAccount.expected_amount}
            />
          ),
        }
      }
    }
  }, [paymentMethod, paymentType, retail, translate, virtualAccount])

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(details.code)
    showSnackbar(translate('global:numberCopied'))
  }, [details.code, translate])

  const handleRenderWaitingForPaymentBadge = useMemo(
    () => (
      <StyledBadge>
        <Paragraph fontSize="m" color="primary_5" fontWeight="bold">
          {`${translate('giftShop:status')}: ${translate(
            'giftShop:transactionStatus',
            {
              context: 'PROCESSED',
            },
          )}`}
        </Paragraph>
      </StyledBadge>
    ),
    [translate],
  )

  const handleRenderInfo = useMemo(
    () => (
      <StyledSection
        style={{
          gap: convertUnit(12),
        }}>
        <StyledDateContainer>
          <StyledDurationText fontSize="m" fontWeight="medium">
            {translate('giftShop:checkoutTransactionDate')}
          </StyledDurationText>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: convertUnit(8),
            }}>
            <Icon color="gray_5" type="clock-ol" size={14} />
            <Paragraph color="gray_5" fontSize="m">
              {parseDate(transactionDate.toISOString(), 'MMMM D, YYYY, HH:mm')}
            </Paragraph>
          </div>
          <div
            style={{
              width: convertUnit(1),
              height: convertUnit(8),
              background: theme.gray_2,
              marginLeft: convertUnit(8),
            }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: convertUnit(8),
            }}>
            <Icon color="primary_5" type="clock" size={14} />
            <div
              style={{
                display: 'flex',
                gap: '8px',
              }}>
              <Paragraph color="primary_5" fontSize="m" fontWeight="bold">
                {parseDate(details.expiration_date, 'MMMM D, YYYY, HH:mm')}
              </Paragraph>
              <Paragraph color="primary_5" fontSize="m">
                (
                {formatHours(
                  timeDiff.hours,
                  timeDiff.minutes,
                  timeDiff.seconds,
                )}
                )
              </Paragraph>
            </div>
          </div>
        </StyledDateContainer>
        {handleRenderWaitingForPaymentBadge}
        <StyledRowSection>
          <PaymentHistoryInfoItem
            title={translate('giftShop:accountName')}
            description={name?.username!}
          />
          <PaymentHistoryInfoItem
            title={translate('giftShop:totalPayment')}
            description={getCurrencyValue(details.expected_amount)}
            totalPrice
            boldDescription
          />
        </StyledRowSection>
        <StyledSection>
          <StyledTitleContainer>
            <StyledTitle fontSize="m" fontWeight="medium">
              {typeDetails().title}
            </StyledTitle>
          </StyledTitleContainer>
          <StyledTitleContainer>
            <Paragraph fontSize="l" fontWeight="bold" color="primary_5">
              {details.code}
            </Paragraph>

            <StyledCopyIcon
              type="copy"
              onClick={copyToClipboard}
              color="primary_5"
            />
          </StyledTitleContainer>
          <StyledMessageContainer>
            <Icon
              type="info"
              size={16}
              style={{
                alignSelf: 'center',
                marginRight: convertUnit(16),
              }}
            />
            <Paragraph
              fontSize="s"
              fontWeight="medium"
              style={{alignSelf: 'center', lineHeight: convertUnit(21)}}>
              {translate('giftShop:transactionVANumberReminder')}
            </Paragraph>
          </StyledMessageContainer>
        </StyledSection>
      </StyledSection>
    ),
    [
      copyToClipboard,
      details.code,
      details.expected_amount,
      details.expiration_date,
      handleRenderWaitingForPaymentBadge,
      name?.username,
      theme.gray_2,
      timeDiff.hours,
      timeDiff.minutes,
      timeDiff.seconds,
      transactionDate,
      translate,
      typeDetails,
    ],
  )

  const handleAccountDetail = useMemo(
    () => (
      <StyledRightSection mode={mode}>
        <Paragraph fontSize="m" fontWeight="medium">
          {translate('giftShop:paymentInstruction')}
        </Paragraph>
        {typeDetails().container}
      </StyledRightSection>
    ),
    [mode, translate, typeDetails],
  )

  return (
    <StyledContainer mode={mode}>
      {handleRenderInfo}
      {handleAccountDetail}
      <ModalLoading visible={loading} />
    </StyledContainer>
  )
}
